/**
 * ONE VISIT'S REPORT, at `/visits/:visitId` (home #21, ops #86), where the visit-done email's "See
 * the report and photos" leads once home says the page is on production; until then ops points that
 * button at `/feedback/<visit_id>`. Built from two artboards in `faithfully-kept-business/design/
 * home/`: the page frame of States-HomeA "4. Photos from a visit", "Home" over the heading as the way
 * back, and the report of States-HomeB "10. Your home is ready, on Messages", with the visit ops
 * pushed in place of the sample one, as GET /api/visits/:id answers it. The words are in `words.ts`.
 *
 * - THE FRAME, AS ARTBOARD 4 DRAWS IT: "Home" where the eyebrow stands, a link back to Home in the
 *   eyebrow's ink with the back chevron, and the page's heading 4px under it; the nav marks Home,
 *   the place a report belongs to, as Feedback's does.
 * - THE HEADING, AS HOME HEADS A CLEAN: "Your home is ready" for today's clean, and "Your home was
 *   cleaned" for one before today (`dayGone`), as Home's card says it; for a visit ops has not pushed
 *   done, and for a read that did not go through, "Your visit" (`VISIT_REPORT.heading`), which says
 *   nothing ops has not.
 * - THE REPORT, AS ARTBOARD 10 DRAWS IT, in its one card: the day; the visit's home among several,
 *   by its label alone (`homeNameOf`, provisional P40); the office's note, "Cleaned by" the cleaner's
 *   first name and each thing noticed, drawn as Home's last clean and Messages' card draw them, from
 *   the same journal (`components/Report.tsx`); "How did we do?" with the four faces, each leading
 *   to Feedback on the clean with that face chosen, as Home's do (`components/FaceLinks.tsx`), where
 *   the Worker offers feedback; and the 24-hour line, on the clean's own day and the day after
 *   (`recleanPromised`), as Feedback draws it, and never past it.
 * - HER ANSWER, AS HOME READS IT (P41, P47): her own newest answer about the clean from home, on the
 *   clean or on its invoice. Once it stands, sent or with ops, the thanks the visit-done email says
 *   once an answer is in, "Thank you for telling us how it went.", in place of the faces: it says
 *   what she did and nothing of what the office will do (CLAUDE.md binding rule 1). Where ops's
 *   receipt says it recorded none of it, past its twelve answers from home on a clean, the faces as
 *   Home draws them with Home's line that ops already has her answers; where it did not go through,
 *   the faces with the line that says so; each with the phone. Nobody else's answer is drawn as hers.
 * - A VISIT OPS HAS NOT PUSHED DONE draws its day and that the report comes after the clean, and
 *   nothing of a report, whatever came with it, so a report is never shown before ops says the clean
 *   is done. One ops says is cancelled, or the cleaner could not get into, says what Schedule says of
 *   it (`VISIT`), in the past for a day gone by, and never that a report comes.
 * - PHOTOS ARE NOT DRAWN YET (home #21). Ops's contract says the journal's `photos` stays empty until
 *   a follow-up builds a signed photo read, and home will then fetch each photo under ops's signed
 *   link and serve it from its own address, so no ops address reaches her. So whatever the journal
 *   carries, the page draws no image and no word about one; `api.ts` does not even read them. Where
 *   they will go is marked below.
 * - WHAT THE WORKER ANSWERS decides the rest, as on Feedback (`Guarded.tsx`): nothing while the
 *   read is out; a 401 is the door; a visit it does not find for her, which for a member is any
 *   visit at a home that is not hers, is said in place under "Your visit", with the phone and the
 *   way Home, never a silent move (the owner, 2026-10-20: every outcome said), and Home's own read
 *   then says where she stands, the page that says to call when ops has revoked her; and a read
 *   that did not go through says so under "Your visit", with the phone.
 * - REACHED THROUGH THE SIGN-IN (P107): a signed-out visitor is drawn the door here, which carries
 *   this page through the sign-in and back (`lib/cameFor.ts`), and the page then asks for her session
 *   and her membership as every page does.
 */
import { useCallback, useId } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getVisit, type CleanAnswer, type Visit, type VisitPlace } from '../api.ts';
import { Backward } from '../components/Chevron.tsx';
import { FaceLinks } from '../components/FaceLinks.tsx';
import { Guarded, type Signed } from '../components/Guarded.tsx';
import { Phone } from '../components/Phone.tsx';
import { Report, reportSays } from '../components/Report.tsx';
import { Shell } from '../components/Shell.tsx';
import { dayInWords } from '../lib/dates.ts';
import { recleanPromised } from '../lib/faces.ts';
import { dayGone, homeNameOf } from '../lib/visits.ts';
import { HOME_PATH } from '../paths.ts';
import { FAILED_BEFORE_PHONE, FEEDBACK, HOME, VISIT, VISIT_REPORT } from '../words.ts';

/** The eyebrow's look, which artboard 4 draws "Home" in. */
const EYEBROW = 'font-mono text-small tracking-widest text-text-emphasis uppercase';

/**
 * "Home", the way back, as artboard 4 draws it where the eyebrow stands: the eyebrow's ink, with the
 * back chevron and an underline to say it is a link, in a box at least 48 tall to take a thumb.
 */
const BACK = `${EYEBROW} inline-flex min-h-tap items-center gap-1 underline underline-offset-4 hover:text-text-body`;

/** The page's heading, as every artboard draws one. */
const HEADING = 'mt-1 font-serif text-h2 font-semibold text-pretty text-text-body';

/** Artboard 10's card: the wash, the brand's rule and 10px between its parts. */
const CARD =
  'flex flex-col gap-2.5 rounded-card border-2 border-border-brand bg-surface-accent-soft p-5';

export function VisitReport(signed: Signed) {
  const { visitId = '' } = useParams();
  const load = useCallback(() => getVisit(visitId), [visitId]);
  return (
    // Keyed by the visit, so another visit's report is another read.
    <Guarded
      key={visitId}
      load={load}
      signed={signed}
      at="home"
      heading={VISIT_REPORT.heading}
      notFound={<NotFound />}
    >
      {(place) => <ReportDrawn place={place} />}
    </Guarded>
  );
}

/**
 * A visit the Worker does not find for her, said in place with the phone, never a silent move to
 * Home; the way Home above it leads to where she stands, the page that says to call after a revoke.
 */
function NotFound() {
  return (
    <Shell at="home">
      <div>
        <Link to={HOME_PATH} className={BACK}>
          <Backward />
          {VISIT_REPORT.home}
        </Link>
        <h1 className={HEADING}>{VISIT_REPORT.heading}</h1>
        <p className="mt-2 text-body text-pretty">
          {VISIT_REPORT.notFound} <Phone after="." />
        </p>
      </div>
    </Shell>
  );
}

/** Whether her answer about the clean stands, as Home draws it: sent from home, or with ops. */
const answerStands = (answer: CleanAnswer | undefined): boolean =>
  answer?.state === 'sent' || answer?.state === 'received';

/**
 * The line under the day of a visit ops has not pushed done: what Schedule says of one cancelled or
 * one the cleaner could not get into, in the past for a day gone by, and otherwise that the report
 * comes after the clean.
 */
function notDoneLine(visit: Visit, today: string): string {
  const gone = dayGone(visit, today);
  switch (visit.status) {
    case 'cancelled':
      return gone ? VISIT.wasCancelled : VISIT.cancelled;
    case 'no_access':
      return gone ? VISIT.couldNotGetInThen : VISIT.couldNotGetIn;
    default:
      return VISIT_REPORT.afterTheClean;
  }
}

function ReportDrawn({ place }: { readonly place: VisitPlace }) {
  const { visit, journal, answer, today } = place;
  const done = visit.status === 'done';
  const day = visit.date === undefined ? null : dayInWords(visit.date);
  const home = homeNameOf(visit, place.homes);
  const heading = !done
    ? VISIT_REPORT.heading
    : dayGone(visit, today)
      ? HOME.wasCleaned
      : HOME.ready;
  const said = reportSays({ journal, cleaner: visit.cleaner });
  const reclean = recleanPromised(visit.date, today);

  return (
    <Shell at="home" payments={place.household.payments}>
      <div>
        <Link to={HOME_PATH} className={BACK}>
          <Backward />
          {VISIT_REPORT.home}
        </Link>
        <h1 className={HEADING}>{heading}</h1>
      </div>

      <section className={CARD}>
        {day !== null || home !== undefined ? (
          <div>
            {day === null ? null : <p className="text-body font-semibold text-text-body">{day}</p>}
            {home === undefined ? null : <p className="text-body text-text-body">{home}</p>}
          </div>
        ) : null}
        {done ? (
          <>
            {said ? (
              <div>
                <Report journal={journal} cleaner={visit.cleaner} small />
              </div>
            ) : null}
            {/*
             * PHOTOS GO HERE, where artboard 10 draws them under the note, once ops's signed photo
             * read is built (home #21, its follow-up; ops's `docs/seams/home.md`, the journal's
             * `photos`): each served from home's own address, never an ops one, with artboard 4's
             * "These photos are for you only. The links last 30 days." and, for expired ones, "Ask
             * for them again" (`journal.photos`). Nothing is drawn until then.
             */}
            <HowDidWeDo visit={visit} answer={answer} />
            {reclean ? (
              <p className="text-small text-pretty text-text-muted">{FEEDBACK.noCharge}</p>
            ) : null}
          </>
        ) : (
          <p className="text-body text-pretty text-text-body">{notDoneLine(visit, today)}</p>
        )}
      </section>
    </Shell>
  );
}

/**
 * "How did we do?" with the four faces, or the thanks once her answer stands, as Home reads it;
 * with the faces, Home's line where ops recorded none of her answer, or where it did not go
 * through, each with the phone. Nothing where the Worker offers no feedback and she has given none.
 */
function HowDidWeDo({
  visit,
  answer,
}: {
  readonly visit: Visit;
  readonly answer: CleanAnswer | undefined;
}) {
  const headingId = useId();
  const stands = answerStands(answer);
  // Ops's receipt said it recorded nothing of it, so it is not the answer that counts.
  const notRecorded = stands && answer?.recorded === false;
  if (stands && !notRecorded) {
    return <p className="text-body text-pretty text-text-body">{VISIT_REPORT.thanks}</p>;
  }
  if (!visit.offers.includes('visit.feedback')) return null;
  const failed = answer?.state === 'refused' || answer?.state === 'stopped';
  const line = notRecorded ? HOME.alreadyBeforePhone : failed ? FAILED_BEFORE_PHONE : null;
  return (
    <div role="group" aria-labelledby={headingId}>
      <h2 id={headingId} className="text-body font-semibold text-text-body">
        {VISIT_REPORT.howDidWeDo}
      </h2>
      <FaceLinks visitId={visit.id} className="mt-2.5" />
      {line === null ? null : (
        <p className="mt-2.5 text-small text-pretty text-text-body">
          {line} <Phone after="." />
        </p>
      )}
    </div>
  );
}
